import { useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import getConfig from "next/config";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { Button } from "@/components/button";
import { Thumbnail } from "@/components/editor/shared/media/video-cards";
import { ContentLibraryDialog } from "@/components/shared/content-library/content-library-dialog";
import { ContentLibrary } from "@/components/shared/content-library/content-library-loader";
import { useContentLibraryTranslations } from "@/components/shared/content-library/use-content-library-translations";
import {
  fetchRecordingsById,
  getRecordingsById,
} from "@/features/recordings/recordings-reducer";
import useAnalytics from "@/hooks/use-analytics";
import useEvent from "@/hooks/use-event";
import { useAppDispatch } from "@/redux/store";

import type { VideoBlockProps } from "../video-component";
import { AdvancedSettings } from "./advanced-settings";

const { publicRuntimeConfig } = getConfig();

export const LibrarySetting = () => {
  const { t, i18n } = useTranslation();
  const {
    event: { organization, space_external_id },
  } = useEvent();
  const { preEventOrganizationSpaces: spacesEnabled } = useFlags();
  const { logAnalytics } = useAnalytics();
  const dispatch = useAppDispatch();
  const translations = useContentLibraryTranslations();

  const spaceExternalId = spacesEnabled
    ? space_external_id ?? undefined
    : undefined;

  const {
    props,
    actions: { setProp },
  } = useNode((query) => ({
    props: query.props as VideoBlockProps,
  }));

  const recording = useSelector(getRecordingsById(props.recordingId));
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  return (
    <div className="grid gap-4">
      {props.recordingId && recording && (
        <RoundedContainer>
          <Thumbnail
            $aspectRatio="wide"
            alt={recording.title || ""}
            src={recording.thumbnail}
          />
        </RoundedContainer>
      )}
      <Button
        size="medium"
        onClick={() => {
          setIsLibraryOpen(true);
          logAnalytics("CANVAS_CONTENT_LIBRARY_OPENED");
        }}
      >
        {t("preference-panel.choose-video")}
      </Button>
      <AdvancedSettings />
      <ContentLibraryDialog
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        unmount={true}
      >
        <ContentLibrary
          options={{
            host: publicRuntimeConfig.HOPIN_ROOT,
            organisationId: organization.external_id,
            spaceExternalId,
            i18n: {
              locale: i18n.language,
              translations,
            },
          }}
          onClose={() => setIsLibraryOpen(false)}
          selectedRecordingIds={
            props.recordingId ? [props.recordingId] : undefined
          }
          allowSingleSelection={true}
          onConfirmSelection={async (recordingIds) => {
            try {
              await dispatch(
                fetchRecordingsById({
                  organizationExternalId: organization.external_id,
                  recordingIds: recordingIds,
                  spaceExternalId,
                })
              ).unwrap();
            } catch {
              // error handled inside recordings reducer slice
            }

            setProp((props: VideoBlockProps) => {
              props.url = null;
              props.recordingId = recordingIds[0];
            });
          }}
        />
      </ContentLibraryDialog>
    </div>
  );
};

const RoundedContainer = styled.div`
  border-radius: 4px;
  overflow: hidden;
`;
